import React from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  Progress,
  ResponseErrorPanel,
  EmptyState,
  Link as BackstageLink,
} from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import { useAsync } from 'react-use';

/**
 * Build the catalog entity URL pointing directly at the Dashboard /
 * Monitoring sub-route, so users landing here from the sidebar see the
 * embedded Grafana view immediately instead of having to click through
 * Overview → Dashboard.
 *
 * The /monitoring route is registered by both DashboardEntityPage
 * (`spec.type: dashboard`, tab title "Dashboard") and ServiceEntityPage
 * (`spec.type: service`, tab title "Monitoring") in EntityPage.tsx, so
 * this URL works uniformly for every entity surfaced on this page.
 */
const entityDashboardRoute = (entity: Entity): string => {
  const kind = (entity.kind ?? 'component').toLocaleLowerCase('en-US');
  const namespace = (entity.metadata.namespace ?? 'default').toLocaleLowerCase('en-US');
  const name = entity.metadata.name.toLocaleLowerCase('en-US');
  return `/catalog/${encodeURIComponent(namespace)}/${encodeURIComponent(kind)}/${encodeURIComponent(name)}/monitoring`;
};

/**
 * Top-level page that lists every catalog entity carrying a
 * `grafana/overview-dashboard` annotation. Entities are filtered by the
 * permission policy server-side (catalog-entity read), so each user only
 * sees the rows their role permits — no extra gating needed in this UI.
 *
 * Clicking the entity name takes the user STRAIGHT TO the entity's
 * Dashboard / Monitoring tab (the embedded Grafana view), not the
 * default Overview tab — landing on Overview from here would be one
 * unnecessary click away from the content the user came to see.
 *
 * Clicking "Open in Grafana" jumps directly to the Grafana dashboard URL
 * (still authenticated via the user's Grafana session, or — once
 * Grafana's OAuth login is locked to admins — only available via the
 * Backstage embed which uses Backstage's SA token).
 */
export const DashboardsPage = () => {
  const catalogApi = useApi(catalogApiRef);

  const { value, loading, error } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        // Any entity that declares an overview dashboard. Permission
        // filtering is applied automatically server-side.
        'metadata.annotations.grafana/overview-dashboard': CATALOG_FILTER_EXISTS,
      },
      // Keep the fields list minimal — we only need name, kind, namespace,
      // description, system, and the annotation. Reduces payload.
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'metadata.description',
        'metadata.annotations.grafana/overview-dashboard',
        'spec.system',
        'spec.type',
      ],
      // Sort alphabetically by name for a stable table ordering.
      order: { field: 'metadata.name', order: 'asc' },
    });
    return response.items;
  }, [catalogApi]);

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="Dashboards" subtitle="Grafana dashboards available to you" />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="Dashboards" />
        <Content>
          <ResponseErrorPanel error={error} />
        </Content>
      </Page>
    );
  }

  const entities = value ?? [];

  if (entities.length === 0) {
    return (
      <Page themeId="tool">
        <Header
          title="Dashboards"
          subtitle="Grafana dashboards available to you"
        />
        <Content>
          <EmptyState
            title="No dashboards available"
            missing="content"
            description="There are no components in the catalog with a Grafana overview dashboard that you are permitted to see. If you expect to see dashboards here, contact a platform admin to check your group membership."
          />
        </Content>
      </Page>
    );
  }

  const columns: TableColumn<Entity>[] = [
    {
      title: 'Component',
      field: 'metadata.name',
      render: (entity: Entity) => (
        // Link lands on the entity's Dashboard / Monitoring tab directly,
        // not the default Overview tab. The deep-link route name
        // `/monitoring` is shared by DashboardEntityPage and
        // ServiceEntityPage; the tab title differs ("Dashboard" vs
        // "Monitoring") but the URL is identical.
        <BackstageLink to={entityDashboardRoute(entity)}>
          {entity.metadata.name}
        </BackstageLink>
      ),
    },
    {
      title: 'Kind',
      field: 'kind',
      width: '90px',
    },
    {
      title: 'Module',
      field: 'spec.system',
      width: '160px',
      render: (entity: Entity) =>
        ((entity.spec as { system?: string } | undefined)?.system) ?? '—',
    },
    {
      title: 'Description',
      field: 'metadata.description',
      render: (entity: Entity) =>
        entity.metadata.description ??
        entity.metadata.title ??
        '',
    },
    {
      title: 'Open in Grafana',
      width: '140px',
      render: (entity: Entity) => {
        const url =
          entity.metadata.annotations?.['grafana/overview-dashboard'];
        if (!url) return null;
        return (
          <BackstageLink to={url} target="_blank" rel="noopener noreferrer">
            View →
          </BackstageLink>
        );
      },
    },
  ];

  return (
    <Page themeId="tool">
      <Header
        title="Dashboards"
        subtitle="Grafana dashboards available to you, by component"
      />
      <Content>
        <Table<Entity>
          title={`Components with dashboards (${entities.length})`}
          columns={columns}
          data={entities}
          options={{
            paging: entities.length > 25,
            pageSize: 25,
            search: true,
            sorting: true,
            actionsColumnIndex: -1,
            padding: 'dense',
          }}
        />
      </Content>
    </Page>
  );
};
